import { useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { getBidAgreementRefTypeFromECID, sandhillsRequest, truncateString } from '@Components/Utilities/SandhillsUtility';
import { styled } from '@mui/material/styles';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ConditionalWrapper from '@Components/Utilities/ConditionalWrapper';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import Countdown from 'react-countdown';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import atoms from '@Components/Utilities/SandhillsAtoms';
import EventDateCalendar from '@Components/Events/EventDateCalendar';
import { useRecoilValue } from 'recoil';

const AuctionLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 5,
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.auction.main,
        boxShadow: 'inset 0 2px 9px rgb(255 255 255 / 30%), inset 0 -2px 6px rgb(0 0 0 / 40%)',
    },
    [`& .${linearProgressClasses.bar}:after`]: {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundImage: '-webkit-gradient(linear,0 0,100% 100%,color-stop(.25,rgba(255,255,255,.2)),color-stop(.25,transparent),color-stop(.5,transparent),color-stop(.5,rgba(255,255,255,.2)),color-stop(.75,rgba(255,255,255,.2)),color-stop(.75,transparent),to(transparent))',
        '-webkit-background-size': '50px 50px',
        '-webkit-animation': 'move 2s linear infinite'
    },
    '@keyframes move': {
        '0%': {
            backgroundPosition: '0 0'
        },
        '100%': {
            backgroundPosition: '25px 25px'
        }
    }
}));

export default function EventListing(props) {
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const linkTarget = !currentSite.IsFramelessSite ? "_blank" : "";
    const phoneNumber = props.FormattedPhoneNumber || props.PhoneNumber;
    const routes = useRecoilValue(atoms.routes);

    const [biddingAgreementData, setBiddingAgreementData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLive, setIsLive] = useState(props.IsLiveEvent);
    const [isSavedToWatchList, setIsSavedToWatchList] = useState(props.IsSavedToWatchList);
    const [showFullDescription, setShowFullDescription] = useState(false);

    const parsedAuctionDateOnly = new Date(props.DisplayAuctionDateOnly);
    const showDescriptionLengthToggle = props.Description && props.Description.length > props.MaxDescriptionLength;

    const isEventEnded = props.IsLiveEventDate && props.Progress >= 100;
    const isSaleCatalogEmpty = props.TotalItems == 0;
    // Rare case, but if a live event doesn't have any listings, then it should redirect to that sale's catalog page
    const isLiveSaleCatalogSet = props.IsLiveEventDate && !isEventEnded && !isSaleCatalogEmpty;

    const onBiddingAgreementDataUpdated = (biddingAgreementDataUpdated) => {
        setBiddingAgreementData(biddingAgreementDataUpdated);
        props.openBiddingAgreementModal?.({
            biddingAgreement: biddingAgreementDataUpdated,
            onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
        });
    };

    const loadEventBiddingAgreement = () => {
        if (!isLoading) {
            if (biddingAgreementData) {
                props.openBiddingAgreementModal?.({
                    biddingAgreement: biddingAgreementData,
                    onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
                });
            } else {
                setIsLoading(true);
                sandhillsRequest({
                    url: routes.GetBiddingAgreementWithRefType.replace("{0}", getBidAgreementRefTypeFromECID(props.EventCategoryID)).replace("{1}", props.Id),
                    errorHandling: {
                        showDetails: true,
                        useConsole: false,
                        useAlert: true
                    }
                }).then((data) => {
                    if (data?.BidAgreement) {
                        setBiddingAgreementData(data);
                        setIsLoading(false);
                        props.openBiddingAgreementModal?.({
                            biddingAgreement: data,
                            onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
                        });
                    } else {
                        alert('An error occurred while retrieving the Terms and Conditions.');
                    }
                }).catch((error) => {
                    setIsLoading(false);
                });
            }
        }
    };

    const biddingAgreementButton = () => {
        return (
            <Button className={"ts-button template-branded-button terms-btn" + ((isEFSale() || props?.IsATStandaloneSale) ? ' ef-btn' : '')} variant='contained' color='equipmentFacts' size='small' sx={{ minWidth: '50px', '&.MuiButton-root': { display: 'flex' } }} onClick={loadEventBiddingAgreement}>
                <i className='fas fa-file-shield' /> <span className='visually-hidden'><TranslationText tKey='sandhills.termsandconditions' /></span>
            </Button>
        );
    }

    const countDownRenderer = ({ days, hours, minutes, seconds, completed }) => {
        if (completed) {
            return <TranslationText tKey='sandhills.tradepubwebsite.mvc.auctionislive' />;
        } else {
            let countDown = '';
            if (days > 0) {
                countDown += days + ' ' + (days > 1 ? translator.translate('sandhills.days') : translator.translate('sandhills.day')) + ' ';
            }
            if (hours > 0) {
                countDown += hours + ' ' + (hours > 1 ? translator.translate('sandhills.hours') : translator.translate('sandhills.hour')) + ' ';
            }
            if (minutes > 0) {
                countDown += minutes + ' ' + (minutes > 1 ? translator.translate('sandhills.minutes') : translator.translate('sandhills.minute'));
            }
            if (days == 0 && hours == 0) {
                if (seconds >= 0) {
                    countDown += ' ' + seconds + ' ' + translator.translate('sandhills.seconds');
                }
            }
            countDown = countDown.trim();
            return (
                <span>{countDown}</span>
            );
        }
    };


    // "Ended" events' sale catalogs should direct to the "auction-results" URL, otherwise, always direct to "upcoming-auctions" URL
    const getSaleCatalogUrl = () => isEventEnded ? props.SaleCatalogUrl.replace("upcoming-auctions", "auction-results") : props.SaleCatalogUrl;

    const getAuctionLink = () => {
        // Offline events won't have a catalog or board
        if (props.IsPhysicalOnlyEvent) return null;

        if (props.IsAuctionTime) {
            if (!isATWeeklySale() && props.IsLiveEventDate) {
                return props.AuctionBoardUrl;
            }

            return props.AuctionTimeUrl;
        }

        // Don't want to link to the board if there's nothing on it
        if (isLiveSaleCatalogSet) return props.AuctionBoardUrl;

        return getSaleCatalogUrl();
    };

    const removeFromWatchlist = () => {
        sandhillsRequest({
            url: routes.EventWatchlistRemove,
            method: 'post',
            data: {
                eventID: props.Id,
                eventCategoryID: props.EventCategoryID
            },
            errorHandling: {
                showDetails: false,
                useConsole: false
            }
        }).then((data) => {
            if (data) {
                setIsSavedToWatchList(false);
                let removals = props.listings.filter(c => c.Id !== props.Id);
                if (props.IsWatchList) {
                    props.setListings({
                        type: 'update-listings',
                        payload: removals
                    });

                    if (props.watchlistDeleteSuccess) {
                        props.watchlistDeleteSuccess();
                    }
                }
            }
        });
    }

    const addToWatchlist = () => {
        sandhillsRequest({
            url: routes.EventWatchlistAdd,
            method: 'post',
            data: {
                eventID: props.Id,
                eventCategoryID: props.EventCategoryID
            },
            errorHandling: {
                showDetails: false,
                useConsole: false
            }
        }).then((data) => {
            if (!data.IsAuthenticated) {
                window.location.href = routes.SSO + '?to=' + encodeURIComponent('https://' + window.location.hostname + routes.EventWatchlistAdd + '?EventID=' + props.Id +'&EventCategoryID=' + props.EventCategoryID +'&to=' + encodeURIComponent(window.location.href));
            } else if (data) {
                setIsSavedToWatchList(true);
            }
        });
    }

    const isATWeeklySale = () => {
        return props.IsAuctionTime && !(props?.IsATStandaloneSale || props?.IsATConsignmentSale);
    };

    const isExclusiveOnlineAuction = () => {
        return (props?.IsATStandaloneSale || props?.IsATConsignmentSale);
    };

    const isEFSale = () => {
        return props.IsEquipmentFacts && !(currentSite?.Site?.IsLivestockMarket || currentSite?.Site?.IsEquineMarket);
    };

    const siteLogo = (() => {
        if (props.IsAuctionTime) {
            return (
                <div className='event-listing__site-logo'>
                    <img src={routes.CDNBaseUrl + 'Images/Icons/AuctionTime/icon.png'} alt="Auction Time Logo" />
                </div>
            );
        } else if (isEFSale()) {
            return (
                <div className='event-listing__site-logo ef'>
                    <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/ef-icon-black.png'} alt="Equipment Facts Logo" />
                </div>
            );
        } else if (currentSite?.Site?.IsLivestockMarket || currentSite?.Site?.IsEquineMarket) {
            return (
                <div className={'event-listing__site-logo' + (currentSite?.Site?.IsLivestockMarket ? ' lsm' : ' em')}>
                    <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/production-sale.png'} alt="Production Sale Logo" />
                </div>
            );
        }
        return <></>;
    })();

    return (
        <div className={'event-listing' + ((props.IsLiveEventDate && !props.HideLiveBorder && ((!props.IsAuctionTime && props.TotalItems > 0) || (props.IsAuctionTime && !props.IsPastEvent))) ? ' event-listing_live-auction' : '') + (props.ShowAltBackground ? ' alt-background' : '')}>
            <div className='event-listing__date'>
                {props.IsLiveEventDate && (props.IsAuctionTime || props.TotalItems != null) &&
                    <>
                        {((!props.IsAuctionTime && props.TotalItems > 0 && !isEventEnded) || (props.IsAuctionTime && !props.IsPastEvent)) ?
                            <div className='event-listing__status'>
                                <strong><i className='fas fa-rss' /></strong>
                                <strong><TranslationText tKey='sandhills.tradepubwebsite.mvc.live' /></strong>
                            </div>
                            :
                            <div className='event-listing__status'>
                                <strong><i className='fas fa-gavel' /></strong>
                                <strong><TranslationText tKey='sandhills.ended' /></strong>
                            </div>}
                    </>}
                <EventDateCalendar
                    DateIndex={parsedAuctionDateOnly.getUTCDate()}
                    MonthIndex={parsedAuctionDateOnly.getUTCMonth()}
                    StartTime={props.StartTime}
                />
            </div>
            <div className={'event-listing__details' + (props.IsRecommendedAuctionsSearch ? ' rec-event' : '')}>
                {!props.HideTag &&
                    <div className='event-listing__tag'>
                        {props.ShowAuctionBranding ?
                            <>
                                {siteLogo}
                                <strong><TranslationText tKey={props.IsAuctionTime ? 'sandhills.tradepubwebsite.mvc.onlineauction' : 'sandhills.tradepubwebsite.mvc.onsiteauction'} /></strong>
                            </>
                            :
                            <strong><TranslationText tKey={props.IsAuctionTime ? 'sandhills.timedauction' : 'sandhills.webcast'} /></strong>}
                    </div>}
                {props.EventTitle &&
                    <div className='event-listing__title'>
                        <ConditionalWrapper condition={getAuctionLink()} wrapper={children => <a href={getAuctionLink()}>{children}</a>}>
                            <strong>{props.EventTitle}</strong>
                        </ConditionalWrapper>
                    </div>}
                {(props.IsAuctionTime || (props.FormattedLocation || phoneNumber)) &&
                    <div className={'event-listing__contact-info' + (!props.IsAuctionTime && isLive ? ' event-listing__contact-info_live-auction' : '')}>
                        {(!props.IsAuctionTime || isExclusiveOnlineAuction()) &&
                            <>
                                {props.FormattedLocation &&
                                    <span className='event-listing__location'>
                                        <strong><i className='fas fa-location-dot' /></strong>
                                        <strong>{props.FormattedLocation}</strong>
                                    </span>}
                                {props.DistanceFrom &&
                                    <span className='distance-away'>
                                        <strong><i className='fas fa-location-crosshairs' /></strong>
                                        <strong>{props.DistanceFrom}</strong>
                                    </span>}
                            </>}
                        {phoneNumber &&
                            <span className='event-listing__phone'>
                                <strong><i className='fas fa-phone' /></strong>
                                <strong><a href={'tel:' + props.PhoneNumber}>{phoneNumber}</a></strong>
                            </span>}
                    </div>}
                {!isLive && !props.IsPastEvent && !isEventEnded &&
                    <>
                        {!props.IsAuctionTime ?
                            <>
                                {props.StartDateTime &&
                                    <div className='event-listing__countdown'>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.auctionbeginsin' />
                                        <span className='event-listing__countdown-text' style={{ marginLeft: '3px' }}>
                                            <strong><Countdown date={new Date(props.StartDateTime)?.getTime()} renderer={countDownRenderer} onComplete={(e) => setIsLive(true)} /></strong>
                                        </span>
                                    </div>}
                            </>
                            :
                            <>
                                {props.StartTime &&
                                    <div className='event-listing__countdown'>
                                        <TranslationText tKey='sandhills.lotsstartsellingat' replaceValues={[`<strong><span class='event-listing__countdown-text'>${props.StartTime}</span></strong>`]} />
                                    </div>}
                            </>}
                    </>}
                {props.Description &&
                    <p className='event-listing__description'>
                        {showDescriptionLengthToggle && !showFullDescription ?
                            <>{truncateString(props.Description, props.MaxDescriptionLength)}</>
                            :
                            <>{props.Description}</>}
                        {showDescriptionLengthToggle &&
                            <a tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setShowFullDescription(prev => !prev); } }} onClick={() => setShowFullDescription(prev => !prev)}>
                                <strong><TranslationText tKey={showFullDescription ? 'sandhills.showless' : 'sandhills.readmore'} /></strong>
                            </a>}
                    </p>}
                {!props.IsAuctionTime && isLive && !isEventEnded &&
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                            <AuctionLinearProgress variant='determinate' value={props.Progress || 0} title={props.ProgressText} />
                        </Box>
                        <Box>
                            <Typography variant='body2'>{`${props.Progress ? Math.round(props.Progress) : 0}%`}</Typography>
                        </Box>
                    </Box>}
                {props.DocumentList && props.DocumentList.length > 0 &&
                    <div className="upcoming-links">
                        {props.DocumentList.map((doc, index) => (
                            <div key={index}>
                                {doc.EventFileType.trim() === 'Supplement Sheet' && (
                                    <a href={doc.FileUrl} target="_blank" aria-label={translator.translate('sandhills.tradesites.mvc.viewsupplementsheet') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                        <TranslationText tKey='sandhills.tradesites.mvc.viewsupplementsheet' /><i className='fas fa-external-link-alt' /></a>
                                )}
                                {doc.EventFileType.trim() === 'Sale Catalog' && (
                                    <a href={doc.FileUrl} target="_blank" aria-label={translator.translate('sandhills.tradesites.mvc.viewsalecatalog') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}><TranslationText tKey='sandhills.tradesites.mvc.viewsalecatalog' /></a>
                                )}
                            </div>
                        ))}
                    </div>}
                {!props.IsPhysicalOnlyEvent &&
                    <div className='event-listing__contact-buttons'>
                        {(isExclusiveOnlineAuction() || (props.IsAuctionTime && !currentSite.IsAuctionTimeAuctionSite)) ?
                            <>
                                {(isSaleCatalogEmpty && props.IsATStandaloneSale) ?
                                    <Button className='ts-button template-branded-button no-catalog-btn' onClick={() => props.openAlertPreferenceModal(props.Id, props.EventCategoryID)}>
                                        {translator.translate('sandhills.catalogcomingsoon')}!
                                        <br />
                                        <span style={{ textDecoration: 'underline' }}>{translator.translate('sandhills.getnotified')}</span>
                                    </Button>
                                    :
                                    <>
                                        {(props.IsLiveEventDate && !isATWeeklySale() && !props.IsPastEvent) &&
                                            <Button className='ts-button template-branded-button red-btn' variant='contained' size='small' sx={{ '&.MuiButton-root': { display: 'flex' } }} href={props.AuctionBoardUrl} target={linkTarget}>
                                                <TranslationText tKey='sandhills.enterauction' />
                                            </Button>}
                                        <Button className='ts-button template-branded-button red-btn' variant='contained' sx={{ '&.MuiButton-root': { display: 'flex' } }} size='small' href={props.AuctionTimeUrl} target={linkTarget}>
                                            <TranslationText tKey={props.IsPastEvent ? 'sandhills.tradepubwebsite.mvc.auctionresults' : (isExclusiveOnlineAuction() ? 'sandhills.auctioncatalog' : 'sandhills.viewdetails')} />
                                        </Button>
                                        {props.HasBidAgreement && !props.IsPastEvent &&
                                            biddingAgreementButton()}
                                    </>
                                }
                            </>
                            :
                            <>
                                {isSaleCatalogEmpty ?
                                    <Button className='ts-button template-branded-button no-catalog-btn' onClick={() => props.openAlertPreferenceModal(props.Id, props.EventCategoryID)}>
                                        {translator.translate('sandhills.catalogcomingsoon')}!
                                        <br />
                                        <span style={{ textDecoration: 'underline' }}>{translator.translate('sandhills.getnotified')}</span>
                                    </Button>
                                    :
                                    <>
                                        {props.IsLiveEventDate && !isEventEnded &&
                                            <Button className='ts-button template-branded-button red-btn' variant='contained' size='small' sx={{ mb: 1, '&.MuiButton-root': { display: 'flex' } }} href={isLiveSaleCatalogSet ? props.AuctionBoardUrl : getSaleCatalogUrl()} target={linkTarget}>
                                                <TranslationText tKey='sandhills.enterauction' />
                                            </Button>}
                                        <Button className={"ts-button template-branded-button" + ((!props.IsLiveEventDate || isEventEnded) ? " red-btn" : (isEFSale() ? ' ef-btn' : " sales-btn"))} variant='contained' color={props.IsLiveEventDate && !isEventEnded && !props.IsPastEvent ? 'equipmentFacts' : undefined} size='small' sx={{ '&.MuiButton-root': { display: 'flex' } }} href={getSaleCatalogUrl()} target={linkTarget}>
                                            <TranslationText tKey={((props.IsPastEvent || isEventEnded) ? 'sandhills.tradepubwebsite.mvc.auctionresults' : (props.IsLiveEventDate ? 'sandhills.auctioncatalog' : 'sandhills.startbidding'))} />
                                        </Button>
                                        {props.HasBidAgreement && !currentSite.IsAuctionTimeAuctionSite && !isEventEnded &&
                                            biddingAgreementButton()}
                                    </>}
                            </>}
                    </div>}
            </div>
            <div className='event-listing__logo'>
                {props.IsAuctionTime && !currentSite.IsAuctionTimeAuctionSite && props.ShowAuctionBranding ?
                    <>
                        {props.LogoUrl ?
                            <>
                                <a className='event-listing__logo-link' href={getAuctionLink()}>
                                    <span className='visually-hidden'>AuctionTime.com</span>
                                    <LazyLoadImage src={props.LogoUrl} alt='AuctionTime Online Auction' className='event-listing__logo-image' />
                                </a>
                                {isExclusiveOnlineAuction() &&
                                    <div className='event-listing__logo-name'>
                                        <ConditionalWrapper condition={props.CompanyUrl} wrapper={children => <a className='event-listing__logo-link' href={props.CompanyUrl}>{children}<span className='visually-hidden'>{props.AuctioneerName}</span></a>}>
                                            <strong>{props.AuctioneerName}</strong>
                                        </ConditionalWrapper>
                                    </div>}
                            </>
                            :
                            <div className='event-listing__logo-name'>
                                <a className='event-listing__logo-link' href={getAuctionLink()}>
                                    <strong>AuctionTime.com</strong>
                                </a>
                            </div>}
                    </>
                    :
                    <>
                        {props.LogoUrl &&
                            <ConditionalWrapper condition={getAuctionLink()} wrapper={children => <a className='event-listing__logo-link' href={getAuctionLink()}>{children}<span className='visually-hidden'>{props.AuctioneerName}</span></a>}>
                                <LazyLoadImage src={props.LogoUrl} alt={props.AuctioneerName} className='event-listing__logo-image' width='217' height='126' />
                            </ConditionalWrapper>}
                        <div className='event-listing__logo-name'>
                            <ConditionalWrapper condition={props.CompanyUrl} wrapper={children => <a className='event-listing__logo-link' href={props.CompanyUrl}>{children}<span className='visually-hidden'>{props.AuctioneerName}</span></a>}>
                                <strong>{props.AuctioneerName}</strong>
                            </ConditionalWrapper>
                        </div>
                    </>}
            </div>
            {(!props.IsAuctionTime || props?.IsATStandaloneSale) &&
                <div className='event-listing__watchlist'>
                    {isSavedToWatchList ?
                        <div className='watchlist-btn watchlist-delete' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); removeFromWatchlist(); } }} aria-label={translator.translate('sandhills.removefromwatchlistwithspace')} onClick={(e) => removeFromWatchlist()} >
                            <i className='fas fa-heart' />
                        </div>
                        :
                        <div className='watchlist-btn watchlist-add' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); addToWatchlist(); } }} aria-label={translator.translate('sandhills.addtowatchlistwithspace')} onClick={(e) => addToWatchlist()} >
                            <i className='far fa-heart' />
                        </div>}
                </div>}
            <Backdrop
                open={isLoading}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress />
            </Backdrop>
        </div>
    );
};